/**
 * useHighlightEditFlow — chat "edit highlight" intent, end to end.
 *
 * State machine:
 *   idle → requesting → pending_decision → applying → idle
 *                └──────────────┴──────────── error → idle
 *
 * On route (from useChatSystem's intent router): a single backend call
 * (/api/selection-edit) returns insert-ready replacement text for the
 * highlighted span, plus the section draft + version captured
 * atomically. The proposal renders as a before/after card with
 * Approve / Reject.
 *
 * Approve applies the change in BOTH places:
 *   1. Word — plain prose goes through ``replaceTextInSection``
 *      (section-scoped search/replace, 255-char-needle-safe,
 *      prefix-hint disambiguation); block content (tables, headings,
 *      paragraph breaks, bold) goes through
 *      ``replaceTextInSectionStyled`` which re-renders the covered
 *      span via the formatter pipeline — the same styling path fresh
 *      section drafts use.
 *   2. SSOT — new content computed by a whitespace-tolerant span
 *      replace over the captured ``section_draft``, submitted through
 *      the version-checked edit endpoint (lifecycle walked to
 *      ``inserted`` first when needed). One retry on version conflict.
 *
 * Reject discards the proposal. Both decisions land as ``approval``
 * EventLog entries so the chat audit trail records the outcome.
 */

import { useCallback, useRef, useState } from "react";
import {
  type RetrievalProvenanceItem,
  type RetrievalSummary,
  type SelectionEditRequestBody,
  type SelectionEditResponse,
  requestSelectionEditStream,
} from "../../api/client";
import type { ApplyStep } from "../components/ApplyProgress";
import type { MentionAttachment } from "../../types";
import { needsFormatterRender } from "../../services/formatter";
import {
  EDIT_ANCHOR_TAG_PREFIX,
  type SectionTableRowWrite,
  anchorCurrentSelection,
  applyTableRowEditsInSection,
  captureTableSelection,
  removeEditAnchors,
  replaceAnchoredSelection,
  replaceTextInSection,
  replaceTextInSectionStyled,
} from "../../services/wordWriter";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import { insertProposalIntoSection } from "../services/proposalInsert";
import { labelForSelectionEditEvent } from "./selectionEditLabels";
import { type TableEditSnippet, buildSnippet, rowToPipe } from "../services/tableEdit";

export interface HighlightEditRequest {
  selectedText: string;
  instruction: string;
  runId: string | null;
  sectionId: string | null;
  sectionNumber: string | null;
  /** Writer-attached source assets (@-mentions) riding on the message —
   *  pre-fetched server-side and embedded in the rewrite prompt. */
  attachments?: MentionAttachment[];
}

export interface HighlightEditProposalState {
  request: HighlightEditRequest;
  response: SelectionEditResponse;
  /** True when the highlighted range was wrapped in a hidden content
   *  control at proposal time — Approve replaces the EXACT range (no
   *  re-search, no ambiguity). False → Approve uses the string path. */
  anchored: boolean;
  /** Content-control tag of the anchor (present even when anchoring
   *  failed, for cleanup idempotence). */
  anchorTag: string;
  /** Revision instructions applied to this proposal, oldest first. Each
   *  "Revise" re-runs the rewrite against the PREVIOUS replacement (the
   *  latest change) plus the unchanged surrounding paragraph as context.
   *  Empty for a fresh proposal. */
  revisions: string[];
  /** Present when the highlight was inside a table: the per-row snippet
   *  diff (rendered as the preview with changed cells highlighted) plus
   *  the SSOT write-back context. When set, Approve applies cell-by-cell
   *  in place instead of the prose span replace. */
  table?: TableEditModel;
}

/** Table-edit proposal payload — the assembled snippet plus what Approve
 *  needs to write both stores. */
export interface TableEditModel {
  snippet: TableEditSnippet;
  /** SSOT draft + version to write the row replacements against (from the
   *  per-row rewrite responses — the same section for every row). */
  sectionDraft: string | null;
  sectionVersion: number | null;
  moduleId: string | null;
  /** Retrieval provenance / tool calls aggregated across the rows. */
  provenance: RetrievalProvenanceItem[];
  toolCalls: Record<string, unknown>[];
  rootSpanId: string | null;
}

// "revising" mirrors "requesting" but keeps the existing proposal card
// mounted (with its buttons disabled) while a "Revise" re-run is in flight,
// so the writer keeps seeing the draft they're refining.
export type HighlightEditPhase = "idle" | "requesting" | "pending_decision" | "applying" | "revising";

/** Snapshot of the decided proposal handed to ``onDecisionRecorded``
 *  so the shell can append the durable ``edit_proposal`` EventLog
 *  entry (drafted output + outcome persist in the thread). */
export interface HighlightEditDecisionRecord {
  instruction: string;
  originalText: string;
  replacementText: string;
  sectionNumber: string | null;
}

export interface UseHighlightEditFlowParams {
  /** Record the decision in the EventLog (shell owns dispatch):
   *  an ``edit_proposal`` entry (durable proposal + outcome) plus
   *  the ``approval`` system note (audit invariant). */
  onDecisionRecorded: (
    decision: "accepted" | "rejected",
    note: string,
    sectionId: string | null,
    proposal: HighlightEditDecisionRecord,
  ) => void;
  /** Surface a writer-readable error (rendered as an error note). */
  onError: (message: string) => void;
  /** Active source library — the backend resolves @-mention attachments
   *  against it. Null/undefined → attachments degrade to placeholders. */
  libraryId?: string | null;
}

export interface UseHighlightEditFlowResult {
  phase: HighlightEditPhase;
  /** The active proposal while phase === "pending_decision" | "applying". */
  proposal: HighlightEditProposalState | null;
  /** The request in flight while phase === "requesting" — lets the
   *  shell name the target section in the streaming status line. */
  activeRequest: HighlightEditRequest | null;
  /** Live status line derived from the rewrite's SSE events (checking for
   *  context, retrieving, rewriting) while phase === "requesting" (first
   *  rewrite) or "revising" (a "Revise" re-run). Null when idle or before
   *  the first event lands. */
  progressLabel: string | null;
  /** Stepped checklist for the post-approve apply pipeline, updated as
   *  ``approve`` advances through its stages. Empty unless phase ===
   *  "applying". Rendered in place of the proposal card during the wait. */
  applySteps: ApplyStep[];
  /** Kick off the flow for a routed edit-highlight intent. */
  start: (req: HighlightEditRequest) => void;
  approve: () => Promise<void>;
  reject: () => void;
  /** Re-run the rewrite against the current proposal's replacement (the
   *  latest change) plus the unchanged surrounding paragraph as context,
   *  applying ``revisionInstruction``. Only valid while
   *  phase === "pending_decision"; leaves the anchor untouched (nothing
   *  has been applied to Word yet) so Approve still targets the original
   *  span. On success the proposal card updates in place with the revised
   *  text; on failure the prior proposal is kept and an error surfaces. */
  revise: (revisionInstruction: string) => void;
}

/** Post-approve apply stages, in execution order: the Word render (the
 *  slow part) then the SSOT write-back. The second stage is present only
 *  when the section is run-tracked. */
const APPLY_STEP_WORD = "Applying the change in the Word document";
const APPLY_STEP_SAVE = "Saving to the record";

/** Unfilled gap markers a rewrite leaves in place when the library has no data
 *  for a cell — mirrors the registry prefixes in draftSanitizer. */
const GAP_MARKER_RE = /\[(?:MISSING INFO|MISSING TABLE|CANNOT RESOLVE)\b/i;

/** Explain WHY a table edit changed nothing, so the writer can tell a genuine
 *  "the library doesn't have this" apart from a bug. Priority: unparseable
 *  rows → library lacked the data (markers still present) → already matches. */
function describeTableNoChange(
  snippet: TableEditSnippet,
  total: number,
  summary: RetrievalSummary | null,
): string {
  const rows = total === 1 ? "row" : "rows";
  if (snippet.skippedRows >= total) {
    return `Asthra couldn't parse the highlighted table ${rows} — nothing was changed.`;
  }
  const markersRemain = snippet.rows.some((r) => r.cells.some((c) => GAP_MARKER_RE.test(c.new)));
  if (markersRemain && (!summary || summary.found === 0)) {
    const gaps = summary?.gaps?.length ? ` Still missing: ${summary.gaps.join("; ")}.` : "";
    return `The source library doesn't contain the data for these cells, so the MISSING INFO flags remain.${gaps}`;
  }
  const skipNote = snippet.skippedRows > 0 ? " (some rows couldn't be parsed as table rows)" : "";
  return `The highlighted table ${rows} already match the source library — nothing to change${skipNote}.`;
}

/** Whitespace-tolerant first-occurrence span replace (mirrors the
 *  backend's count_occurrences normalization). Exported for tests. */
export function replaceSpanInDraft(draft: string, original: string, replacement: string): string | null {
  const needle = original.replace(/\s+/g, " ").trim();
  if (!needle) return null;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s+");
  const re = new RegExp(escaped);
  if (!re.test(draft)) return null;
  return draft.replace(re, () => replacement);
}

/** Prefix hint for the string-search fallback: the text immediately
 *  before the FIRST occurrence of ``original`` in the stored draft.
 *  First-occurrence on purpose — it matches replaceSpanInDraft's SSOT
 *  write-back, so Word and the backend stay consistent when the text
 *  appears more than once. Exported for tests. */
export function computeContextHint(draft: string | null, original: string): { prefix: string } | undefined {
  if (!draft) return undefined;
  const needle = original.replace(/\s+/g, " ").trim();
  if (!needle) return undefined;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s+");
  const m = new RegExp(escaped).exec(draft);
  if (!m) return undefined;
  const prefix = draft.slice(Math.max(0, m.index - 80), m.index).trim();
  return prefix ? { prefix } : undefined;
}

/** Fold any applied revision instructions into the recorded instruction so
 *  the durable ``edit_proposal`` / ``approval`` entries show what was actually
 *  asked (original → each revision). Exported for tests. */
export function foldRevisionInstruction(base: string, revisions: readonly string[]): string {
  if (!revisions.length) return base;
  return [base, ...revisions].join(" → revised: ");
}

export function useHighlightEditFlow(params: UseHighlightEditFlowParams): UseHighlightEditFlowResult {
  const { onDecisionRecorded, onError, libraryId } = params;
  const [phase, setPhase] = useState<HighlightEditPhase>("idle");
  const [proposal, setProposal] = useState<HighlightEditProposalState | null>(null);
  const [activeRequest, setActiveRequest] = useState<HighlightEditRequest | null>(null);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  // Post-approve apply progress: the ordered stage labels for the current
  // approve + the index of the in-flight stage. Gated on phase below.
  const [applyLabels, setApplyLabels] = useState<string[]>([]);
  const [applyStepIndex, setApplyStepIndex] = useState(-1);
  // Monotonic token so a stale async response can't resurrect a flow
  // the writer already rejected / replaced.
  const flowTokenRef = useRef(0);

  // Shared rewrite stream: drive the live status line from intermediate SSE
  // events and resolve with the assembled response from the terminal
  // ``complete`` event. Used by both ``start`` (first rewrite) and ``revise``
  // (refine the prior draft). ``token`` gates progress updates so a superseded
  // flow can't move the status line.
  const rewriteStream = useCallback(
    (body: SelectionEditRequestBody, token: number, labelPrefix?: string): Promise<SelectionEditResponse> =>
      new Promise<SelectionEditResponse>((resolve, reject) => {
        let settled = false;
        requestSelectionEditStream(body, (event) => {
          const type = String(event.type ?? "");
          if (type === "complete") {
            settled = true;
            resolve({
              replacement_text: String(event.replacement_text ?? ""),
              original_text: String(event.original_text ?? body.selected_text),
              module_id: (event.module_id as string | null) ?? null,
              section_number: (event.section_number as string | null) ?? null,
              section_version: (event.section_version as number | null) ?? null,
              occurrences_in_section: Number(event.occurrences_in_section ?? 0),
              section_draft: (event.section_draft as string | null) ?? null,
              retrieval_provenance: (event.retrieval_provenance as RetrievalProvenanceItem[]) ?? [],
              tool_call_log: (event.tool_call_log as Record<string, unknown>[]) ?? [],
              root_span_id: (event.root_span_id as string | null | undefined) ?? null,
              retrieval_summary: (event.retrieval_summary as RetrievalSummary | null | undefined) ?? null,
            });
          } else if (type === "error") {
            settled = true;
            reject(new Error(String(event.error ?? "rewrite failed")));
          } else if (flowTokenRef.current === token) {
            const label = labelForSelectionEditEvent(event);
            if (label) setProgressLabel(labelPrefix ? `${labelPrefix} · ${label}` : label);
          }
        }).then(
          () => {
            if (!settled) reject(new Error("The rewrite stream ended without a result."));
          },
          (err) => {
            if (!settled) reject(err);
          },
        );
      }),
    [],
  );

  // Table branch of ``start``: rewrite each highlighted row one at a time
  // (the same engine a prose highlight edit uses) and assemble a snippet
  // diff. The table was already anchored by captureTableSelection (``tag``)
  // so Approve can re-find it. Throws are caught by the caller.
  const runTableEdit = useCallback(
    async (
      req: HighlightEditRequest,
      tag: string,
      capture: Awaited<ReturnType<typeof captureTableSelection>>,
      token: number,
    ): Promise<void> => {
      const total = capture.rows.length;
      const results: { rowIndex: number; oldCells: string[]; rawReplacement: string | null }[] = [];
      let sectionDraft: string | null = null;
      let sectionVersion: number | null = null;
      let moduleId: string | null = null;
      let rootSpanId: string | null = null;
      const provenance: RetrievalProvenanceItem[] = [];
      const toolCalls: Record<string, unknown>[] = [];
      // Aggregate each row's retrieval outcome so a no-op can say WHY (library
      // genuinely lacked the data vs. rows already matching).
      let aggSummary: RetrievalSummary | null = null;

      for (let i = 0; i < total; i++) {
        const row = capture.rows[i];
        setProgressLabel(`Editing row ${i + 1} of ${total}…`);
        let rawReplacement: string | null = null;
        try {
          const resp = await rewriteStream(
            {
              selected_text: rowToPipe(row.cells),
              instruction: req.instruction,
              run_id: req.runId ?? undefined,
              module_id: req.sectionId ?? undefined,
              section_number: req.sectionNumber ?? undefined,
              library_id: libraryId ?? undefined,
              attachments: req.attachments,
            },
            token,
            `Row ${i + 1}/${total}`,
          );
          if (flowTokenRef.current !== token) {
            void removeEditAnchors(tag);
            return;
          }
          rawReplacement = resp.replacement_text ?? null;
          // First section context wins — every row is the same section.
          if (sectionDraft === null && resp.section_draft) sectionDraft = resp.section_draft;
          if (sectionVersion === null && resp.section_version != null) sectionVersion = resp.section_version;
          if (moduleId === null && resp.module_id) moduleId = resp.module_id;
          if (rootSpanId === null && resp.root_span_id) rootSpanId = resp.root_span_id;
          if (resp.retrieval_provenance?.length) provenance.push(...resp.retrieval_provenance);
          if (resp.tool_call_log?.length) toolCalls.push(...resp.tool_call_log);
          if (resp.retrieval_summary) {
            aggSummary = aggSummary
              ? {
                  items: aggSummary.items + resp.retrieval_summary.items,
                  found: aggSummary.found + resp.retrieval_summary.found,
                  gaps: [...aggSummary.gaps, ...resp.retrieval_summary.gaps],
                }
              : { ...resp.retrieval_summary, gaps: [...resp.retrieval_summary.gaps] };
          }
        } catch (err) {
          if (flowTokenRef.current !== token) {
            void removeEditAnchors(tag);
            return;
          }
          // One row failing shouldn't sink the whole edit — record it as a
          // no-result skip and continue with the rest.
          rawReplacement = null;
        }
        results.push({ rowIndex: row.rowIndex, oldCells: row.cells, rawReplacement });
      }

      if (flowTokenRef.current !== token) {
        void removeEditAnchors(tag);
        return;
      }

      // Build the diff over the WHOLE table (not just the highlighted rows):
      // highlighted rows use the model's rewrite, every other row is a
      // passthrough (old === new → no highlighted cells). Shows the full table
      // in one shot; only genuinely changed cells are marked/applied.
      const rewriteByRow = new Map(results.map((r) => [r.rowIndex, r.rawReplacement]));
      const headerIsHighlighted = capture.rows.some((r) => r.rowIndex === 0);
      const headerCells = headerIsHighlighted ? null : (capture.allRows[0]?.cells ?? null);
      const dataRows = headerIsHighlighted ? capture.allRows : capture.allRows.filter((r) => r.rowIndex !== 0);
      const fullResults = dataRows.map((row) => ({
        rowIndex: row.rowIndex,
        oldCells: row.cells,
        rawReplacement: rewriteByRow.has(row.rowIndex)
          ? (rewriteByRow.get(row.rowIndex) ?? null)
          : rowToPipe(row.cells),
      }));
      const snippet = buildSnippet(fullResults, headerCells);
      if (snippet.changedCells === 0) {
        void removeEditAnchors(tag);
        setPhase("idle");
        setActiveRequest(null);
        setProgressLabel(null);
        onError(describeTableNoChange(snippet, total, aggSummary));
        return;
      }

      // Synthesize a SelectionEditResponse for the decision record + audit:
      // the before/after of just the changed rows as pipe markdown.
      const changed = snippet.rows.filter((r) => !r.skippedReason && r.cells.some((c) => c.changed));
      const originalBlock = changed.map((r) => rowToPipe(r.cells.map((c) => c.old))).join("\n");
      const replacementBlock = changed.map((r) => rowToPipe(r.cells.map((c) => c.new))).join("\n");

      const response: SelectionEditResponse = {
        replacement_text: replacementBlock,
        original_text: originalBlock,
        module_id: moduleId,
        section_number: req.sectionNumber,
        section_version: sectionVersion,
        occurrences_in_section: 1,
        section_draft: sectionDraft,
        retrieval_provenance: provenance,
        tool_call_log: toolCalls,
        root_span_id: rootSpanId,
        retrieval_summary: aggSummary,
      };

      setProgressLabel(null);
      setProposal({
        request: req,
        response,
        anchored: true,
        anchorTag: tag,
        revisions: [],
        table: { snippet, sectionDraft, sectionVersion, moduleId, provenance, toolCalls, rootSpanId },
      });
      setActiveRequest(null);
      setPhase("pending_decision");
    },
    [rewriteStream, libraryId, onError],
  );

  const start = useCallback(
    (req: HighlightEditRequest) => {
      const token = ++flowTokenRef.current;
      const anchorTag = `${EDIT_ANCHOR_TAG_PREFIX}${Date.now()}-${token}`;
      setPhase("requesting");
      setProposal(null);
      setActiveRequest(req);
      setProgressLabel(null);
      void (async () => {
        // Sweep any anchor orphaned by a crashed prior flow before we
        // place a new one (table or prose).
        try {
          await removeEditAnchors();
        } catch {
          // non-fatal — anchoring is an optimization
        }

        // Table branch: if the highlight is inside an editable table, edit
        // the affected ROWS one at a time and present a snippet-diff
        // preview (captureTableSelection anchors the table for Approve).
        // Anything not usable as a table falls through to the prose flow.
        let tableCapture: Awaited<ReturnType<typeof captureTableSelection>> | null = null;
        try {
          tableCapture = await captureTableSelection();
        } catch {
          tableCapture = null;
        }
        if (tableCapture?.usable && tableCapture.rows.length > 0) {
          try {
            await runTableEdit(req, anchorTag, tableCapture, token);
          } catch (err) {
            void removeEditAnchors(anchorTag);
            if (flowTokenRef.current !== token) return;
            setPhase("idle");
            setActiveRequest(null);
            setProgressLabel(null);
            const detail = err instanceof Error ? err.message : String(err);
            onError(`Couldn't edit the highlighted table rows: ${detail}`);
          }
          return;
        }

        // Prose flow — anchor the live selection so Approve can replace the
        // exact range. anchorCurrentSelection verifies the live selection
        // still matches the captured text — a moved cursor skips anchoring
        // (string fallback still works).
        let anchored = false;
        try {
          anchored = await anchorCurrentSelection(anchorTag, req.selectedText);
        } catch {
          anchored = false; // anchor is an optimization, never a blocker
        }
        try {
          // Stream the rewrite: intermediate SSE events drive the live
          // status line; the terminal ``complete`` event carries the full
          // response. ``error`` rejects; a stream that ends without either
          // is treated as a failure.
          const response = await rewriteStream(
            {
              selected_text: req.selectedText,
              instruction: req.instruction,
              run_id: req.runId ?? undefined,
              module_id: req.sectionId ?? undefined,
              section_number: req.sectionNumber ?? undefined,
              library_id: libraryId ?? undefined,
              attachments: req.attachments,
            },
            token,
          );
          if (flowTokenRef.current !== token) {
            if (anchored) void removeEditAnchors(anchorTag);
            return;
          }
          setProgressLabel(null);
          setProposal({ request: req, response, anchored, anchorTag, revisions: [] });
          setActiveRequest(null);
          setPhase("pending_decision");
        } catch (err) {
          if (anchored) void removeEditAnchors(anchorTag);
          if (flowTokenRef.current !== token) return;
          setPhase("idle");
          setActiveRequest(null);
          setProgressLabel(null);
          const detail = err instanceof Error ? err.message : String(err);
          onError(`Couldn't rewrite the highlighted text: ${detail}`);
        }
      })();
    },
    [onError, libraryId, rewriteStream, runTableEdit],
  );

  // Re-run the rewrite as a REVISION of the current proposal. The original
  // span stays the target in Word (anchor untouched — nothing applied yet);
  // the backend refines the prior replacement (``previous_draft``) against
  // the writer's new instruction plus the unchanged surrounding paragraph.
  const revise = useCallback(
    (revisionInstruction: string) => {
      const current = proposal;
      if (!current || phase !== "pending_decision") return;
      // Revise is a single-span refinement — not defined for the multi-row
      // table proposal (its preview hides the affordance anyway).
      if (current.table) return;
      const trimmed = revisionInstruction.trim();
      if (!trimmed) return;
      const token = ++flowTokenRef.current;
      const { request: req, response } = current;
      setPhase("revising");
      setProgressLabel(null);
      void (async () => {
        try {
          const revised = await rewriteStream(
            {
              // Original span (still in the document) — occurrences /
              // section context / write-back all key off this, not the
              // prior draft.
              selected_text: response.original_text,
              instruction: trimmed,
              // The latest change the writer is refining.
              previous_draft: response.replacement_text,
              run_id: req.runId ?? undefined,
              module_id: req.sectionId ?? undefined,
              section_number: req.sectionNumber ?? undefined,
              library_id: libraryId ?? undefined,
              attachments: req.attachments,
            },
            token,
          );
          if (flowTokenRef.current !== token) return;
          setProgressLabel(null);
          setProposal({
            // Keep the original span as the anchored target; only the
            // replacement text (and the revision log) change.
            request: req,
            response: { ...revised, original_text: response.original_text },
            anchored: current.anchored,
            anchorTag: current.anchorTag,
            revisions: [...current.revisions, trimmed],
          });
          setPhase("pending_decision");
        } catch (err) {
          if (flowTokenRef.current !== token) return;
          // Revision failed — keep the prior proposal so the writer can
          // decide on it or try a different revision.
          setProgressLabel(null);
          setProposal(current);
          setPhase("pending_decision");
          const detail = err instanceof Error ? err.message : String(err);
          onError(`Couldn't revise the rewrite: ${detail}`);
        }
      })();
    },
    [proposal, phase, libraryId, rewriteStream, onError],
  );

  const reject = useCallback(() => {
    if (!proposal) return;
    flowTokenRef.current++;
    // Fire-and-forget anchor cleanup — the doc must not keep the
    // hidden control after the proposal dies.
    void removeEditAnchors(proposal.anchorTag).catch(() => undefined);
    const sectionLabel = proposal.request.sectionNumber ? `§${proposal.request.sectionNumber}` : "the highlighted text";
    onDecisionRecorded("rejected", `Rejected the rewrite of ${sectionLabel}.`, proposal.request.sectionId, {
      instruction: foldRevisionInstruction(proposal.request.instruction, proposal.revisions),
      originalText: proposal.response.original_text,
      replacementText: proposal.response.replacement_text,
      sectionNumber: proposal.request.sectionNumber,
    });
    setProposal(null);
    setPhase("idle");
  }, [proposal, onDecisionRecorded]);

  const approve = useCallback(async () => {
    if (!proposal || phase !== "pending_decision") return;
    const token = ++flowTokenRef.current;
    const { request: req, response } = proposal;
    const notes: string[] = [];

    // Table branch — apply the per-row cell edits in place (drop-in, no
    // paragraph deletion) via the table anchor, then replace each changed
    // row's markdown in the SSOT draft. Reuses the same two-stage progress
    // + audit as the prose path.
    if (proposal.table) {
      const table = proposal.table;
      // Build section-scoped writes — one per changed row — carrying each
      // row's ORIGINAL cell values so Approve can re-find the exact table by
      // matching cells (no whole-table content control, whose removal blanks
      // the table). More changed rows = a tighter, safer table match.
      const sectionWrites: SectionTableRowWrite[] = [];
      for (const row of table.snippet.rows) {
        if (row.skippedReason || !row.cells.some((c) => c.changed)) continue;
        sectionWrites.push({
          rowIndex: row.rowIndex,
          cells: row.cells.map((c) => c.new),
          changedColumns: row.cells.map((c, i) => (c.changed ? i : -1)).filter((i) => i >= 0),
          originalCells: row.cells.map((c) => c.old),
        });
      }
      const runTracked = !!(req.runId && req.sectionId && table.sectionDraft);
      setApplyLabels(runTracked ? [APPLY_STEP_WORD, APPLY_STEP_SAVE] : [APPLY_STEP_WORD]);
      setApplyStepIndex(0);
      setPhase("applying");
      try {
        // 1. Word — re-find the table by section + matching original cells,
        //    then write only the changed cells in place (never a whole-table
        //    content control, which blanks the table when removed).
        if (!req.sectionNumber) {
          if (flowTokenRef.current !== token) return;
          setPhase("pending_decision");
          onError("Couldn't locate the table to apply the changes (no section context) — nothing was changed.");
          return;
        }
        const applied = await applyTableRowEditsInSection(req.sectionNumber, sectionWrites);
        if (applied === "not_found") {
          if (flowTokenRef.current !== token) return;
          setPhase("pending_decision");
          onError("Couldn't locate the table to apply the changes (it may have moved) — nothing was changed.");
          return;
        }

        // 2. SSOT — replace each changed row's markdown in the stored draft,
        //    then one version-checked write for the whole section.
        if (req.runId && req.sectionId && table.sectionDraft) {
          setApplyStepIndex(1);
          let draft = table.sectionDraft;
          let missed = 0;
          for (const row of table.snippet.rows) {
            if (row.skippedReason || !row.cells.some((c) => c.changed)) continue;
            const oldPipe = rowToPipe(row.cells.map((c) => c.old));
            const newPipe = rowToPipe(row.cells.map((c) => c.new));
            const next = replaceSpanInDraft(draft, oldPipe, newPipe);
            if (next) draft = next;
            else missed += 1;
          }
          if (draft !== table.sectionDraft) {
            const result = await insertProposalIntoSection(
              { runId: req.runId, moduleId: req.sectionId, sectionNumber: req.sectionNumber },
              draft,
              {
                skipWordInsert: true,
                expectedVersion: table.sectionVersion ?? undefined,
                transactions: table.provenance,
                toolCalls: table.toolCalls,
                rootSpanId: table.rootSpanId,
                instruction: req.instruction,
                selectedText: req.selectedText,
              },
            );
            if (!result.ssotUpdated) notes.push(result.message);
          }
          if (missed > 0) {
            notes.push(
              `${missed} row${missed === 1 ? "" : "s"} weren't found in the stored draft, so the backend record wasn't fully updated`,
            );
          }
        } else if (req.runId) {
          notes.push("the section isn't tracked in this run, so only the document was updated");
        } else {
          notes.push("no run context — the change is not tracked in the backend");
        }

        if (flowTokenRef.current !== token) return;
        const sectionLabel = req.sectionNumber ? `§${req.sectionNumber}` : "the document";
        const suffix = notes.length > 0 ? ` (${notes.join("; ")})` : "";
        onDecisionRecorded(
          "accepted",
          `Applied table edits to ${sectionWrites.length} row${sectionWrites.length === 1 ? "" : "s"} in ${sectionLabel}${suffix}.`,
          req.sectionId,
          {
            instruction: foldRevisionInstruction(req.instruction, proposal.revisions),
            originalText: response.original_text,
            replacementText: response.replacement_text,
            sectionNumber: req.sectionNumber,
          },
        );
        setProposal(null);
        setPhase("idle");
      } catch (err) {
        void removeEditAnchors(proposal.anchorTag).catch(() => undefined);
        if (flowTokenRef.current !== token) return;
        setPhase("pending_decision");
        const detail = err instanceof Error ? err.message : String(err);
        onError(`Applying the table edits failed: ${detail}`);
      }
      return;
    }

    // Post-approve stage checklist: Word render first, then the SSOT
    // write-back when the section is run-tracked (else Word-only).
    const runTracked = !!(req.runId && req.sectionId && response.section_draft);
    setApplyLabels(runTracked ? [APPLY_STEP_WORD, APPLY_STEP_SAVE] : [APPLY_STEP_WORD]);
    setApplyStepIndex(0);
    setPhase("applying");

    // Sanitized once, used for BOTH the Word apply and the SSOT span
    // replace so the two stores never diverge on marker cleanup.
    const cleanedReplacement = sanitizeDraftForWord(response.replacement_text);

    try {
      // 1. Word apply — section-scoped when we know the section,
      //    else replace at the writer's (possibly moved) selection is
      //    not safe, so we require a section number for the Word write.
      //    Block content (tables / headings / paragraph breaks / bold)
      //    renders through the formatter pipeline — the same styling
      //    path fresh section drafts use — so markdown never lands as
      //    literal characters; plain prose keeps the inline splice
      //    (preserves surrounding intra-paragraph formatting).
      const styled = needsFormatterRender(cleanedReplacement);

      // 1a. Anchored replace — the exact range wrapped at proposal
      //     time. No re-search: robust to duplicate text, whitespace
      //     drift, multi-paragraph selections, and edits elsewhere.
      //     "anchor_lost" (control deleted / content drifted) falls
      //     through to the section-scoped string path.
      let wordApplied = false;
      if (proposal.anchored) {
        const anchoredResult = await replaceAnchoredSelection(
          proposal.anchorTag,
          response.original_text,
          cleanedReplacement,
          styled,
        );
        wordApplied = anchoredResult === "replaced";
      }

      // 1b. String fallback — section-scoped search with the stored-
      //     draft prefix hint disambiguating duplicate occurrences
      //     (first-occurrence bias matches the SSOT write-back below).
      if (!wordApplied) {
        if (!req.sectionNumber) {
          setPhase("pending_decision");
          onError("Couldn't resolve which section the highlight is in — nothing was changed.");
          return;
        }
        const hint = computeContextHint(response.section_draft, response.original_text);
        const result = styled
          ? await replaceTextInSectionStyled(req.sectionNumber, response.original_text, cleanedReplacement, hint)
          : await replaceTextInSection(req.sectionNumber, response.original_text, cleanedReplacement, hint);
        if (result === "not_found") {
          setPhase("pending_decision");
          onError(
            `Couldn't find the highlighted text in §${req.sectionNumber} — it may have been edited. Nothing was changed.`,
          );
          return;
        }
        if (result === "table_conflict") {
          // The highlight reaches into a table the splice paths won't touch
          // (e.g. a merged-cell table the row flow declined). Refuse rather
          // than corrupt the table.
          setPhase("pending_decision");
          onError(
            "Part of the highlight is inside a table — nothing was changed. Highlight the table cells on their own to edit them row by row.",
          );
          return;
        }
        if (result === "ambiguous_first_replaced") {
          notes.push("the text appeared more than once; the first occurrence was updated");
        }
      }

      // 2. SSOT write-back — only when the section is run-tracked.
      if (req.runId && req.sectionId && response.section_draft) {
        setApplyStepIndex(1);
        const newContent = replaceSpanInDraft(response.section_draft, response.original_text, cleanedReplacement);
        if (newContent) {
          const result = await insertProposalIntoSection(
            {
              runId: req.runId,
              moduleId: req.sectionId,
              sectionNumber: req.sectionNumber,
            },
            newContent,
            // Word is already updated (span replace above) — the
            // service should only do the SSOT half. Retrieval provenance
            // rides along so it lands on the section now that the edit
            // is actually being applied.
            {
              skipWordInsert: true,
              expectedVersion: response.section_version ?? undefined,
              transactions: response.retrieval_provenance,
              toolCalls: response.tool_call_log,
              rootSpanId: response.root_span_id,
              instruction: req.instruction,
              selectedText: req.selectedText,
            },
          );
          if (!result.ssotUpdated) {
            notes.push(result.message);
          }
        } else {
          notes.push(
            "the stored draft no longer contains the highlighted text, so the backend record was left unchanged",
          );
        }
      } else if (req.runId) {
        notes.push("the section isn't tracked in this run, so only the document was updated");
      } else {
        notes.push("no run context — the change is not tracked in the backend");
      }

      if (flowTokenRef.current !== token) return;
      const sectionLabel = req.sectionNumber ? `§${req.sectionNumber}` : "the document";
      const suffix = notes.length > 0 ? ` (${notes.join("; ")})` : "";
      onDecisionRecorded("accepted", `Applied the rewrite in ${sectionLabel}${suffix}.`, req.sectionId, {
        instruction: foldRevisionInstruction(req.instruction, proposal.revisions),
        originalText: response.original_text,
        replacementText: response.replacement_text,
        sectionNumber: req.sectionNumber,
      });
      setProposal(null);
      setPhase("idle");
    } catch (err) {
      // Unknown Word state after a throw — sweep the anchor so a
      // hidden control can't linger in the writer's document.
      void removeEditAnchors(proposal.anchorTag).catch(() => undefined);
      if (flowTokenRef.current !== token) return;
      setPhase("pending_decision");
      const detail = err instanceof Error ? err.message : String(err);
      onError(`Applying the rewrite failed: ${detail}`);
    }
  }, [proposal, phase, onDecisionRecorded, onError]);

  // Project the stage labels + active index into the checklist. Gated on
  // phase so a reverted/superseded flow never shows stale steps.
  const applySteps: ApplyStep[] =
    phase === "applying"
      ? applyLabels.map((label, i) => ({
          id: `hl-apply-${i}`,
          label,
          state: i < applyStepIndex ? "done" : i === applyStepIndex ? "active" : "pending",
        }))
      : [];

  return { phase, proposal, activeRequest, progressLabel, applySteps, start, approve, reject, revise };
}
